import type { DocumentNode } from 'graphql';
import * as _ from 'lodash-es';
import type { Paths } from 'type-fest';
import type {
  ApolloQueryResult,
  MaybeMasked,
  OperationVariables,
  QueryResult,
} from '@apollo/client';

type GeneratedFetchMore<TData, TVariables extends OperationVariables> = (
  variables?: Partial<QueryResult<TData, TVariables>['variables']>,
) => Promise<ApolloQueryResult<TData>>;

type GenerateFetchMoreOptions = {
  afterCursorOffset?: number;
  afterCursorVariableKey?: string;
};

const generateFetchMore = <TData, TVariables extends OperationVariables>(
  fetchMore: QueryResult<TData, TVariables>['fetchMore'],
  query: DocumentNode,
  { pageInfo: { hasNextPage, endCursor } }: Connection,
  originalVariables = {},
  options: GenerateFetchMoreOptions = {},
): GeneratedFetchMore<MaybeMasked<TData>, TVariables> | null => {
  if (!hasNextPage || !fetchMore) {
    return null;
  }

  const { afterCursorOffset, afterCursorVariableKey } = {
    afterCursorVariableKey: 'afterCursor',
    ...options,
  };

  const afterCursor =
    afterCursorOffset != null ? offsetToCursor(afterCursorOffset) : endCursor;

  return (variables) =>
    fetchMore({
      query,
      variables: {
        ...originalVariables,
        ...variables,
        [afterCursorVariableKey]: afterCursor || null,
      },
    });
};

export const offsetToCursor = (offset: number) =>
  btoa(`arrayconnection:${offset}`);

// eslint-disable-next-line import/no-unused-modules
export const cursorToOffset = (cursor: string) =>
  parseInt(atob(cursor).replace('arrayconnection:', ''), 10);

const moveCursor = (cursor: string | null | undefined, diff: number) => {
  if (!cursor) {
    return offsetToCursor(0);
  }

  const oldOffset = cursorToOffset(cursor);
  const newOffset = Math.max(0, oldOffset + diff);

  return offsetToCursor(newOffset);
};

type EdgeNode = { id: string; __typename?: string };

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export type Connection = {
  __typename?: string | null;
  pageInfo: {
    hasNextPage?: boolean;
    endCursor?: string | null;
    beforeCursor?: string;
    __typename?: string;
  };
  edges?: Array<{
    node?: EdgeNode | null;
    cursor?: string | null;
    __typename?: string;
  } | null> | null;
  totalCount?: number | null;
};

const createEdge = <C extends Connection>(
  { __typename: connectionTypename }: C,
  node: EdgeNode,
  cursor: string,
) => {
  return {
    node,
    cursor,
    __typename: connectionTypename?.replace(/Connection/, 'Edge'),
  };
};

const mapNodes = <C extends Connection>(
  connection: C,
  updater: (node: EdgeNode) => EdgeNode,
): C => {
  const { edges } = connection;
  return {
    ...connection,
    edges: edges?.map((edge) =>
      edge?.node
        ? {
            ...edge,
            node: updater(edge.node),
          }
        : edge,
    ),
  };
};

const ConnectionHelper = {
  generateFetchMore: <TData, TVariables extends object>(
    query: DocumentNode,
    hookResult: Pick<
      QueryResult<TData, TVariables>,
      'loading' | 'variables' | 'fetchMore' | 'data'
    >,
    keyPath: Paths<TData>,
    options: GenerateFetchMoreOptions = {},
  ) => {
    const { variables, fetchMore, data = {}, loading } = hookResult;
    const connection = _.get(data, keyPath);

    return !loading && connection
      ? generateFetchMore<TData, TVariables>(
          fetchMore,
          query,
          connection,
          variables,
          options,
        )
      : null;
  },

  updateNode<C extends Connection>(connection: C, newNode: { id: string }): C {
    let isUpdated = false;
    const newConnection = mapNodes(connection, (node) => {
      if (newNode.id === node.id) {
        isUpdated = true;
        return {
          ...node,
          ...newNode,
        };
      }

      return node;
    });

    return isUpdated ? newConnection : connection;
  },

  removeNode<C extends Connection>(connection: C, removedNode: EdgeNode): C {
    const { pageInfo } = connection;
    const { endCursor } = pageInfo;

    const edges = connection.edges ?? [];

    const newEdges = edges.filter((edge) => edge?.node?.id !== removedNode.id);
    const lastEdge = newEdges[newEdges.length - 1];
    const newEndCursor =
      lastEdge?.cursor || moveCursor(endCursor, newEdges.length - edges.length);

    return {
      ...connection,
      edges: newEdges,
      pageInfo: {
        ...pageInfo,
        endCursor: newEndCursor,
      },
      ...(_.isNumber(connection.totalCount) && {
        totalCount: connection.totalCount - 1,
      }),
    };
  },

  insertNodeAfter<C extends Connection>(
    connection: C,
    newNode: EdgeNode,
    { skipUpdate }: { skipUpdate?: boolean } = {},
  ): C {
    if (!connection || !newNode) return connection;

    const updated = skipUpdate
      ? connection
      : ConnectionHelper.updateNode(connection, newNode);
    if (updated !== connection) return updated;

    const { pageInfo, edges } = connection;
    const { endCursor } = pageInfo;
    const newNodeCursor = moveCursor(endCursor, 1);

    return {
      ...connection,
      edges: [...(edges ?? []), createEdge(connection, newNode, newNodeCursor)],
      pageInfo: {
        ...pageInfo,
        endCursor: newNodeCursor,
      },
      ...(_.isNumber(connection.totalCount) && {
        totalCount: connection.totalCount + 1,
      }),
    };
  },

  insertNodeBefore<C extends Connection>(
    connection: C,
    newNode: EdgeNode,
    { skipUpdate }: { skipUpdate?: boolean } = {},
  ): C {
    if (!connection || !newNode) return connection;

    const updated = skipUpdate
      ? connection
      : ConnectionHelper.updateNode(connection, newNode);
    if (updated !== connection) return updated;

    const { pageInfo, edges } = connection;
    const { beforeCursor, endCursor } = pageInfo;
    const newNodeCursor = moveCursor(beforeCursor, -1);

    const newEdges = [
      createEdge(connection, newNode, newNodeCursor),
      ...(edges ?? []),
    ];

    return {
      ...connection,
      edges: newEdges,
      pageInfo: {
        ...pageInfo,
        endCursor: moveCursor(endCursor, 1),
      },
      ...(_.isNumber(connection.totalCount) && {
        totalCount: connection.totalCount + 1,
      }),
    };
  },
};

export default ConnectionHelper;
